import React, { useState, useRef } from 'react';
import { useAuth } from './context/AuthContext';
import logoImage from './images/WazzaScore_Logo2.png';
import { useLocation, useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, InputGroup } from 'react-bootstrap';
import { Eye, EyeSlash, PersonFill, LockFill, Google, Apple } from 'react-bootstrap-icons';

function Login() {
  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(null); // 'google' | 'apple' | null

  const { login, loginWithGoogle, loginWithApple, register, authError, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const fromRef = useRef(location.state?.from?.pathname || '/upload');
  const from = fromRef.current;

  const isRegister = mode === 'register';

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!username.trim() || !password.trim()) {
      alert(`Please enter both ${isRegister ? 'email' : 'username'} and password.`);
      return;
    }

    if (isRegister && password !== confirmPassword) {
      alert("Passwords do not match.");
      return;
    }

    try {
      const action = isRegister ? register : login;
      const user = await action(username.trim(), password.trim());
      if (user) {
        navigate(from, { replace: true });
      }
    } catch (error) {
      console.error(`${isRegister ? 'Register' : 'Login'} failed:`, error);
    }
  };

  const handleOauth = async (providerName, providerFn) => {
    setOauthLoading(providerName);
    try {
      const user = await providerFn();
      if (user) {
        navigate(from, { replace: true });
      }
    } catch (error) {
      console.error(`${providerName} sign-in failed:`, error);
    } finally {
      setOauthLoading(null);
    }
  };

  const toggleMode = () => {
    setMode(isRegister ? 'login' : 'register');
    setConfirmPassword('');
  };

  const toggleShowPassword = () => setShowPassword(!showPassword);

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1a1a1a 0%, #0d2818 50%, #1a1a1a 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
    >
      <Container>
        <Row className="justify-content-center">
          <Col xs={12} sm={10} md={8} lg={5} xl={4}>
            <Card
              className="shadow-lg border-0"
              style={{
                backgroundColor: 'rgba(30, 30, 30, 0.95)',
                borderRadius: '16px',
                backdropFilter: 'blur(10px)'
              }}
            >
              <Card.Body className="p-4 p-md-5">
                {/* Logo */}
                <div className="text-center mb-4">
                  <img
                    src={logoImage}
                    alt="WazzaScore Logo"
                    style={{
                      maxWidth: '180px',
                      height: 'auto',
                      marginBottom: '16px'
                    }}
                  />
                  <h4 className="text-white mb-1">
                    {isRegister ? 'Create Account' : 'Welcome Back'}
                  </h4>
                  <p className="text-secondary mb-0">
                    {isRegister
                      ? 'Sign up to access the coach portal'
                      : 'Sign in to your coach portal'}
                  </p>
                  {!isRegister && (
                    <p className="text-secondary small mt-2 mb-0" style={{ fontStyle: 'italic' }}>
                      Use the same credentials as the WazzaScore mobile app.
                    </p>
                  )}
                </div>

                {/* OAuth providers (works for both login and register) */}
                <div className="d-flex gap-2 mb-3">
                  <Button
                    variant="light"
                    size="lg"
                    onClick={() => handleOauth('google', loginWithGoogle)}
                    disabled={oauthLoading !== null || authLoading}
                    style={{
                      flex: 1,
                      backgroundColor: '#fff',
                      color: '#1a1a1a',
                      fontWeight: 600,
                      padding: '12px',
                      borderRadius: '8px',
                      border: 'none',
                    }}
                  >
                    {oauthLoading === 'google' ? (
                      <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                    ) : (
                      <>
                        <Google className="me-2" />
                        Google
                      </>
                    )}
                  </Button>
                  <Button
                    variant="dark"
                    size="lg"
                    onClick={() => handleOauth('apple', loginWithApple)}
                    disabled={oauthLoading !== null || authLoading}
                    style={{
                      flex: 1,
                      backgroundColor: '#000',
                      color: '#fff',
                      fontWeight: 600,
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid #404040',
                    }}
                  >
                    {oauthLoading === 'apple' ? (
                      <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                    ) : (
                      <>
                        <Apple className="me-2" />
                        Apple
                      </>
                    )}
                  </Button>
                </div>

                {/* OR divider */}
                <div className="d-flex align-items-center my-3" style={{ gap: 8 }}>
                  <div style={{ flex: 1, height: 1, backgroundColor: '#404040' }} />
                  <span className="text-secondary small">OR</span>
                  <div style={{ flex: 1, height: 1, backgroundColor: '#404040' }} />
                </div>

                {/* Error Alert */}
                {authError && (
                  <Alert variant="danger" className="mb-4">
                    {authError}
                  </Alert>
                )}

                {/* Email/password form */}
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-light small fw-semibold">
                      Email
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text
                        style={{
                          backgroundColor: '#2a2a2a',
                          border: '1px solid #404040',
                          color: '#1cac53'
                        }}
                      >
                        <PersonFill />
                      </InputGroup.Text>
                      <Form.Control
                        type="email"
                        placeholder="Enter your email"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        required
                        autoComplete={isRegister ? 'email' : 'username'}
                        style={{
                          backgroundColor: '#2a2a2a',
                          border: '1px solid #404040',
                          color: '#fff'
                        }}
                        className="py-2"
                      />
                    </InputGroup>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="text-light small fw-semibold">
                      Password
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text
                        style={{
                          backgroundColor: '#2a2a2a',
                          border: '1px solid #404040',
                          color: '#1cac53'
                        }}
                      >
                        <LockFill />
                      </InputGroup.Text>
                      <Form.Control
                        type={showPassword ? 'text' : 'password'}
                        placeholder={isRegister ? 'Choose a password (min 6 chars)' : 'Enter your password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        minLength={isRegister ? 6 : undefined}
                        autoComplete={isRegister ? 'new-password' : 'current-password'}
                        style={{
                          backgroundColor: '#2a2a2a',
                          border: '1px solid #404040',
                          color: '#fff'
                        }}
                        className="py-2"
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={toggleShowPassword}
                        style={{
                          backgroundColor: '#2a2a2a',
                          border: '1px solid #404040',
                          color: '#888'
                        }}
                      >
                        {showPassword ? <EyeSlash /> : <Eye />}
                      </Button>
                    </InputGroup>
                  </Form.Group>

                  {isRegister && (
                    <Form.Group className="mb-3">
                      <Form.Label className="text-light small fw-semibold">
                        Confirm Password
                      </Form.Label>
                      <InputGroup>
                        <InputGroup.Text
                          style={{
                            backgroundColor: '#2a2a2a',
                            border: '1px solid #404040',
                            color: '#1cac53'
                          }}
                        >
                          <LockFill />
                        </InputGroup.Text>
                        <Form.Control
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Re-enter your password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                          autoComplete="new-password"
                          style={{
                            backgroundColor: '#2a2a2a',
                            border: '1px solid #404040',
                            color: '#fff'
                          }}
                          className="py-2"
                        />
                      </InputGroup>
                    </Form.Group>
                  )}

                  <Form.Group className="mb-4">
                    <Form.Check
                      type="checkbox"
                      id="showPassword"
                      label="Show password"
                      checked={showPassword}
                      onChange={toggleShowPassword}
                      className="text-secondary small"
                    />
                  </Form.Group>

                  <div className="d-grid">
                    <Button
                      type="submit"
                      size="lg"
                      disabled={authLoading}
                      style={{
                        backgroundColor: '#1cac53',
                        border: 'none',
                        fontWeight: '600',
                        padding: '12px',
                        borderRadius: '8px'
                      }}
                      className="mb-3"
                    >
                      {authLoading ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          {isRegister ? 'Creating account...' : 'Signing in...'}
                        </>
                      ) : (
                        isRegister ? 'Create Account' : 'Sign In'
                      )}
                    </Button>
                  </div>
                </Form>

                {/* Mode toggle */}
                <div className="text-center mt-3">
                  <small className="text-secondary">
                    {isRegister ? 'Already have an account?' : "Don't have an account?"}{' '}
                    <Button
                      variant="link"
                      onClick={toggleMode}
                      className="p-0 align-baseline"
                      style={{ color: '#1cac53', textDecoration: 'none', fontWeight: 600 }}
                    >
                      {isRegister ? 'Sign in' : 'Register'}
                    </Button>
                  </small>
                </div>

                {/* Footer */}
                <div className="text-center mt-4">
                  <small className="text-secondary">
                    Coach Portal Access Only
                  </small>
                </div>
              </Card.Body>
            </Card>

            {/* Bottom text */}
            <p className="text-center text-secondary mt-4 small">
              &copy; {new Date().getFullYear()} WazzaScore. All rights reserved.
            </p>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default Login;
